<script setup>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import ToggleInput from '@/Shared/ToggleInput.vue'
import Pagination from '@/Shared/Pagination.vue'
import { ref, watch } from 'vue'
import { useDate } from '@/Composables/useDate'
import Portrait from '@/Shared/Portrait.vue'
import SearchInput from '@/Shared/SearchInput.vue'

const props = defineProps({
  lessons: {
    type: Object,
    default: () => {}
  },
  filters: {
    type: Object,
    default: () => ({
      search: ''
    })
  },
  levels: {
    type: Array,
    default: () => []
  },
  coaches: {
    type: Array,
    default: () => []
  },
  weekdays: {
    type: Array,
    default: () => []
  }
})

function lessonWeekdays(lesson) {
  return props.weekdays
    .filter((w) => lesson.weekdays.includes(w.value))
    .map((w) => w.name)
    .join(', ')
}

const search = ref(props.filters.search)

props.lessons.data.forEach((lesson) => {
  watch(
    () => lesson.enabled,
    () => {
      router.put(`/lessons/${lesson.id}/enable`, {
        preserveState: true,
        preserveScroll: true
      })
    }
  )
})

const breadcrumbs = [{ label: 'Clases' }, { label: 'Horarios' }]

const emptyState = {
  title: 'No hay clases todavía',
  description: 'Crea tu primera clase para comenzar a gestionar los horarios del club',
  buttonText: 'Crear clase',
  buttonUrl: '/lessons/create',
  icon: 'Calendar'
}
</script>

<template>
  <Portrait
    title="Clases"
    subtitle="Gestiona las clases del club y sus horarios"
    :breadcrumbs="breadcrumbs"
  >
    <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <SearchInput
        v-model="search"
        class="w-full sm:w-64"
        placeholder="Buscar clases..."
        url="/lessons"
        :debounce-time="250"
        :only="['lessons']"
      />

      <Link class="btn-indigo flex items-center justify-center" href="/lessons/create">
        <Icon name="Plus" class="h-5 w-5 mr-2" />
        <span>Nueva clase</span>
      </Link>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="lesson in lessons.data"
        :key="lesson.id"
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-indigo-300 cursor-pointer"
        @click="$inertia.visit(`/lessons/${lesson.id}/edit`)"
      >
        <!-- Cabecera de la tarjeta -->
        <div class="p-4 border-b border-gray-100 flex justify-between items-center">
          <div class="flex items-center space-x-3">
            <!-- Avatar con nivel -->
            <div
              class="rounded-full h-10 w-10 flex items-center justify-center font-bold text-white"
              :style="`background-color: ${lesson.level.color || '#4F46E5'};`"
            >
              {{ lesson.level.name.charAt(0) }}
            </div>
            <!-- Información del nivel -->
            <div>
              <h3 class="font-semibold text-gray-900">{{ lesson.level.name }}</h3>
              <div class="text-xs text-gray-500">
                {{ lessonWeekdays(lesson) }}
              </div>
            </div>
          </div>
          <!-- Estado de la clase -->
          <div @click.stop>
            <ToggleInput v-model="lesson.enabled" />
          </div>
        </div>

        <!-- Información principal -->
        <div class="p-4">
          <!-- Horario -->
          <div class="mb-3 flex items-center">
            <Icon name="Clock" class="h-4 w-4 text-gray-400 mr-2" />
            <span class="text-sm text-gray-700">
              {{ lesson.time_start }} - {{ lesson.time_end }}
            </span>
          </div>

          <!-- Fechas -->
          <div class="mb-3 flex items-center">
            <Icon name="Calendar" class="h-4 w-4 text-gray-400 mr-2" />
            <span class="text-sm text-gray-700">
              {{ useDate(lesson.date_start).format('DD/MM/YYYY') }} -
              {{ useDate(lesson.date_end).format('DD/MM/YYYY') }}
            </span>
          </div>

          <!-- Entrenador -->
          <div class="mb-3 flex items-center">
            <Icon name="User" class="h-4 w-4 text-gray-400 mr-2" />
            <span class="text-sm text-gray-700">{{ lesson.coach.player.fullname }}</span>
          </div>

          <!-- Alumnos -->
          <div class="flex items-center">
            <Icon name="Users" class="h-4 w-4 text-gray-400 mr-2" />
            <span class="text-sm text-gray-700">
              {{ lesson.students ? lesson.students.length : 0 }} /
              {{ lesson.max_students || 0 }} alumnos
            </span>
          </div>
        </div>

        <!-- Pie de la tarjeta -->
        <div
          class="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center"
        >
          <span class="text-xs text-gray-500">ID: {{ lesson.id }}</span>
          <span class="text-indigo-600 text-sm font-medium flex items-center">
            Editar
            <Icon name="Pencil" class="h-4 w-4 ml-1" />
          </span>
        </div>
      </div>

      <!-- Estado vacío -->
      <div
        v-if="lessons.data.length === 0"
        class="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center"
      >
        <div class="flex flex-col items-center justify-center max-w-md mx-auto">
          <div class="bg-indigo-50 p-4 rounded-full mb-4">
            <Icon :name="emptyState.icon" class="h-10 w-10 text-indigo-400" />
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ emptyState.title }}</h3>
          <p class="text-gray-500 mb-6">{{ emptyState.description }}</p>
          <Link class="btn-indigo flex items-center justify-center" :href="emptyState.buttonUrl">
            <Icon name="Plus" class="h-5 w-5 mr-2" />
            <span>{{ emptyState.buttonText }}</span>
          </Link>
        </div>
      </div>
    </div>
    <Pagination class="mt-6" :links="lessons.links" />
  </Portrait>
</template>
